/**
 * RecentEnrollmentPanel — dashboard panel from Figma 9174:279063.
 *
 * Dark rounded card hosting a purple peanut-shaped "Recent enrollment" badge
 * floating on the left, and a featured CourseCard on the right showing the
 * learner's most recent assignment.
 *
 * Picks the featured course in this priority:
 *   1. First upcoming course (the natural "recent enrollment" — newly assigned,
 *      not yet started).
 *   2. First in_progress.
 *   3. First finalized (completed or expired).
 *
 * The CTA wording adapts to the bucket so the upcoming case shows "Wait for
 * the start date" exactly as the Figma specifies.
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ClockIcon } from '@heroicons/react/24/outline';
import { EASE_OUT } from '@/lib/motion';
import { useImageUrl } from '../../services/uploads';
import { toDisplayBuckets, type DisplayBucket } from '../../utils/courseBuckets';
import type { components } from '../../types/learnerApi';

type CourseCard = components['schemas']['CourseCard'];
type CourseBuckets = components['schemas']['CourseBuckets'];

interface RecentEnrollmentPanelProps {
  buckets: CourseBuckets;
}

type FeaturedBucket = Extract<DisplayBucket, 'not_started' | 'in_progress' | 'finalized'>;

function pickFeatured(buckets: CourseBuckets): { course: CourseCard; bucket: FeaturedBucket } | null {
  const display = toDisplayBuckets(buckets);
  if (display.not_started.length) return { course: display.not_started[0], bucket: 'not_started' };
  if (display.in_progress.length) return { course: display.in_progress[0], bucket: 'in_progress' };
  if (display.finalized.length) return { course: display.finalized[0], bucket: 'finalized' };
  return null;
}

function formatPublishedDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const dd = d.getDate().toString().padStart(2, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  return `Published ${dd}/${mm}`;
}

function formatDuration(minutes: number | null | undefined): string | null {
  if (typeof minutes !== 'number' || minutes <= 0) return null;
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const RecentEnrollmentPanel: React.FC<RecentEnrollmentPanelProps> = ({ buckets }) => {
  const reduceMotion = useReducedMotion();
  const featured = useMemo(() => pickFeatured(buckets), [buckets]);

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.05 }}
      className="relative rounded-[20px] bg-grey-100 dark:bg-grey-800 px-4 sm:px-5 lg:px-6 py-3 sm:py-4 lg:py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6"
    >
      {/* Mobile heading: bold purple "Recent Enrollment" replaces the peanut badge */}
      <h2 className="lg:hidden text-xl sm:text-2xl font-extrabold text-primary-500 leading-tight">
        Recent Enrollment
      </h2>

      {/* Desktop peanut badge — vertically centered alongside the featured card */}
      <RecentEnrollmentBadge className="hidden lg:block" />

      {/* Featured course */}
      <div className="w-full max-w-[420px] lg:max-w-none lg:w-[440px] shrink-0">
        {featured ? (
          <FeaturedCourseCard course={featured.course} bucket={featured.bucket} />
        ) : (
          <EmptyFeaturedCard />
        )}
      </div>
    </motion.section>
  );
};

export default RecentEnrollmentPanel;

// ─── Peanut "Recent enrollment" badge ────────────────────────────────────────
// Inlines the path from Figma node 6446:50787's vector asset.
// The path is authored vertical (91×425); rotating it 90° via an SVG <g>
// transform yields the horizontal "dumbbell" silhouette: small knot on the
// left, big pill on the right hosting the label.

const BADGE_PATH =
  'M76.2065 332.32C71.7109 327.495 68.795 320.888 68.5752 313.752C68.5633 313.376 68.5574 312.993 68.5574 312.603C68.5574 312.255 68.5574 311.733 68.599 311.051C68.9197 304.597 71.5505 298.651 75.6542 294.328C84.8296 284.651 90.6021 270.636 90.6021 255.027V52.8699C90.5962 23.671 70.4043 0 45.4972 0C20.596 0 0.404128 23.671 0.404128 52.8699V255.034C0.404128 270.643 6.17663 284.658 15.352 294.335C19.4497 298.658 22.0866 304.604 22.4073 311.058C22.4429 311.74 22.4488 312.262 22.4488 312.61C22.4488 313.383 22.4251 314.135 22.3776 314.88C21.9618 321.577 19.135 327.697 14.8947 332.215C5.70747 342.004 -0.0472181 356.137 0.000291932 371.843C0.0834345 400.569 19.9071 424.309 44.4044 424.985C70.0361 425.695 91 401.543 91 371.662C91 356.081 85.2928 342.067 76.2065 332.32Z';

function RecentEnrollmentBadge({ className = '' }: { className?: string }) {
  return (
    <div className={`relative w-full max-w-[340px] aspect-[425/91] ${className}`.trim()}>
      <svg
        viewBox="0 0 425 91"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        {/* Rotate the vertical authoring path 90° into horizontal orientation:
           translate(425 0) rotate(90) maps the small knot to the left edge
           and the big pill to the right. */}
        <g transform="translate(425 0) rotate(90)">
          <path d={BADGE_PATH} className="fill-primary-350" />
        </g>
      </svg>

      {/* Label — centered over the larger bulb (~60% from the left, after
         the small knot ends near 22% of the badge width). */}
      <span className="absolute inset-y-0 left-[20%] right-0 flex items-center justify-center px-4 sm:px-5 text-primary-ink-350 font-bold text-base sm:text-lg lg:text-[19px] leading-tight whitespace-nowrap">
        Recent enrollment
      </span>
    </div>
  );
}

// ─── Featured course card ────────────────────────────────────────────────────

interface FeaturedCourseCardProps {
  course: CourseCard;
  bucket: FeaturedBucket;
}

function FeaturedCourseCard({ course, bucket }: FeaturedCourseCardProps) {
  const [coverUrl] = useImageUrl(course.image_url ?? null);
  const duration = formatDuration(course.estimated_duration_minutes);
  const published = formatPublishedDate(course.publish_date);

  const cta = ctaForBucket(bucket);
  const href = `/learn/courses/${course.assignment_id ?? course.course_id}`;

  return (
    <Link
      to={href}
      className="group flex flex-col lg:flex-row lg:items-stretch rounded-2xl bg-background-primary dark:bg-grey-800 shadow-[0_12px_24px_-4px_rgba(189,188,201,0.18),0_0_2px_rgba(189,188,201,0.2)] overflow-hidden hover:shadow-[0_16px_32px_-4px_rgba(189,188,201,0.25),0_0_3px_rgba(189,188,201,0.25)] active:scale-[0.99] transition-[transform,box-shadow] duration-150"
    >
      {/* Cover */}
      <div className="relative pt-2 px-2 lg:p-2 lg:pr-0 lg:w-[176px] lg:self-center shrink-0">
        <div className="aspect-[240/192] lg:aspect-[240/150] rounded-md overflow-hidden bg-text-tertiary/10">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              aria-hidden="true"
              className="w-full h-full"
              style={{
                background:
                  'linear-gradient(135deg, rgb(var(--color-primary) / 0.85) 0%, rgb(var(--color-secondary) / 0.85) 100%)',
              }}
            />
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-1.5 px-4 pt-4 pb-5 lg:py-3 lg:justify-center min-w-0">
        {duration && (
          <span className="inline-flex items-center gap-1.5 px-2 h-6 w-fit rounded-md bg-text-tertiary/10 text-text-secondary text-xs font-medium">
            <ClockIcon className="w-4 h-4 opacity-60" />
            {duration}
          </span>
        )}
        <h3 className="text-sm sm:text-[15px] font-semibold text-text-primary leading-snug line-clamp-2">
          {course.title ?? 'Untitled course'}
        </h3>
        {published && (
          <p className="text-[13px] font-light text-primary-200">{published}</p>
        )}
        <div className="pt-2 lg:pt-1">
          <span
            className="inline-flex items-center justify-center min-h-[30px] min-w-[64px] px-2 py-1 rounded-full bg-secondary-500 text-[13px] font-bold leading-[22px] active:scale-[0.97] transition-transform duration-150"
          >
            {cta}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ctaForBucket(bucket: FeaturedBucket): string {
  switch (bucket) {
    case 'not_started':
      return 'Start learning';
    case 'in_progress':
      return 'Continue learning';
    case 'finalized':
      return 'Review course';
  }
}

function EmptyFeaturedCard() {
  return (
    <div className="rounded-2xl bg-background-primary/85 dark:bg-grey-800/60 border border-white/10 px-5 py-6 text-center text-sm text-text-secondary">
      You don't have any recent enrollments yet — your trainer will assign
      courses here as they go.
    </div>
  );
}
